import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Separator } from '../../Shared/Separator/Separator';
import { ApiActionBox } from '../ApiAction/ApiActionBox';
import { QueryKey } from '@/Config/api/queryKey';
import {
  HttpActionDto,
  HttpActionFromTextDto,
  HttpRequestAIActionFromTextMode,
} from '@/Domain/api/Action/dto/Action.dto';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type HttpRequestModalParams = {
  mode: HttpRequestAIActionFromTextMode;
  httpRequestAIActionFromText: HttpActionFromTextDto | null;
};

export const HttpRequestAIActionFromTextDialog = () => {
  const { t } = useTranslation('http_request_ai_action', { keyPrefix: 'from_text' });
  const { isOpen, closeModal, params } = useModalStore();

  const { mode, httpRequestAIActionFromText } =
    ((params ?? null) as HttpRequestModalParams | null) || {};

  const title =
    mode == HttpRequestAIActionFromTextMode.CREATED ? t('title_success') : t('title_failed');
  const description =
    mode == HttpRequestAIActionFromTextMode.CREATED ? t('description_success') : '';

  const { data: action } = useQuery<HttpActionDto>({
    queryKey: [
      QueryKey.actions.httpRequest.get,
      httpRequestAIActionFromText?.http_request_ai_action?.id,
    ],
  });

  if (!httpRequestAIActionFromText) {
    return null;
  }

  return (
    <Dialog
      open={isOpen(MODAL_NAMES.HTTP_REQUEST_AI_ACTION_FROM_TEXT_MODAL)}
      onOpenChange={closeModal}
    >
      <DialogContent
        className="w-[70vw] max-w-[750px] max-h-[85vh] flex flex-col gap-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="w-lg">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Separator />

        <div className="overflow-auto custom-scroll" data-testid="http-ai-action-from-text-dialog">
          <div
            className={classNames('space-y-6', {
              'mt-6': !!action,
              'mt-2': !action,
            })}
          >
            {action && (
              <div className="w-64 items-start text-left">
                <ApiActionBox key={action.id} action={action} isPreview />
              </div>
            )}

            <section className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">
                {t('creation_details')}
              </p>
              <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-muted-foreground whitespace-pre-wrap">
                {httpRequestAIActionFromText.explainer_message}
              </div>
            </section>

            <section className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">
                {t('sources')}
              </p>

              {!httpRequestAIActionFromText.citations ||
              httpRequestAIActionFromText.citations.length === 0 ? (
                <p className="text-sm text-foreground-subtle italic">{t('no_sources')}</p>
              ) : (
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {httpRequestAIActionFromText.citations.map((citation, index) => (
                    <li
                      key={`${citation.url}.${index}`}
                      className="flex gap-2 rounded-md border border-neutral-200 bg-card p-3"
                    >
                      <div className="mt-0.5">
                        <Icon icon="mdi:link-variant" className="h-4 w-4 text-foreground-subtle" />
                      </div>
                      <div className="flex flex-col">
                        <a
                          href={citation.url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-blue-600 hover:underline break-all"
                        >
                          {citation.url}
                        </a>
                        <p className="text-xs text-foreground-subtle mt-1">
                          {citation.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
